/**
 * Created by JIA on 2022/1/19.
 */
import { Alert, AppState, NativeEventEmitter, PermissionsAndroid, Platform } from "react-native";
import PropTypes from "prop-types";
import _ from "lodash";

import { t } from "../../../i18n/index";
import DebugLogger from "../../../../utils/DebugLogger";
import LocationHelper from "../../../../utils/LocationHelper";
import AppConfig from "../../../config/AppConfig";
import SessionHelper from "../../../../utils/SessionHelper";
import moment from "moment";
import ApiService from "../../../services/ApiService";
import { i18nKey, recognizeServiceTypeEnum, reviewServiceTypeEnum } from "./SpeechCommonHelper";
import RNSpeechRecognizeClass from "./NativeSpeechRecognize/RNSpeechRecognize.web";
import * as Helper from "../../../utils/Helper";
import Constants from "../../../config/Constants";

const RNSpeechRecognize = new RNSpeechRecognizeClass();
const DEFAULT_RECOGNIZE_TIME_OUT = Constants.SPEECH_CONSTANT.DEFAULT_RECOGNIZE_TIME_OUT;

//region [Event handlers]
const eventEmitter = new NativeEventEmitter(RNSpeechRecognize);
const eventMap = {
    onStatChange: "onStatChange",
    onVolumeChange: "onVolumeChange",
};

const addEventListener = (event, handler) => {
    const mappedEvent = eventMap[event];
    if (mappedEvent) {
        let listener;
        listener = eventEmitter.addListener(mappedEvent, handler);
        _subscriptions.set(handler, listener);
        return {
            remove: () => removeEventListener(event, handler),
        };
    } else {
        DebugLogger.log(`Trying to subscribe to unknown event: "${event}"`);
        return {
            remove: () => {},
        };
    }
};

const removeEventListener = (type, handler) => {
    const listener = _subscriptions.get(handler);
    if (!listener) {
        return;
    }
    listener.remove();
    _subscriptions.delete(handler);
};

const removeAllListeners = () => {
    _subscriptions.forEach((listener, key, map) => {
        listener.remove();
        map.delete(key);
    });
};
//endregion

//region [Enum and i18n]
//in native code use,
// const serviceTypeEnum = {
//     Azure: 0, //微软语音评测, 不可用
//     XunFei: 1, //讯飞语音评测
//     XunFeiASR: 2, //讯飞语音识别, 无时长限制
//     XunFeiIAT: 3, //讯飞语音识别, 最长60s
//     AliYunIAT: 4, //阿里云语音识别, 最长60s
//     AliYunIntIAT: 5, //阿里云国际语音识别, 最长60s
//     AzureIAT: 6, //Azure语音识别, 最长60s, 美国服务器
//     SingSound: 7, //先声语音评测
// };

const _subscriptions = new Map();

let internalInfoObj = {
    forReview: false,
    reviewServiceType: reviewServiceTypeEnum.XunFei,
    recognizeServiceType: recognizeServiceTypeEnum.AliYunIAT,
    referenceText: "",
    referenceMarkText: "",
    referenceSyllableArray: [],
    enableSpeechRecognize: true,
    wavFilePath: "",
    currentAppState: "active",
    proxyAddress: "",
    recognizeTimeout: DEFAULT_RECOGNIZE_TIME_OUT,
    useProxy: false,
    lang: undefined,
    aliYunToken: {},
    aliYunIntToken: {},
    singSoundToken: {},
    customHandler: {
        setReferenceSyllables: () => {},
        setReferenceText: () => {},
        handleResult: () => {},
    },
};

//endregion

//region [IOS callback method helper]
const iosCallbackFn = callbackFN => {
    return (error, events) => {
        if (error) {
            DebugLogger.log(error);
        } else {
            if (events && events.length > 0) {
                callbackFN(events[0]);
            }
        }
    };
};

const androidCallbackFN = callbackFN => {
    return callbackFN;
};

const callbackHandler = Platform.OS === "ios" ? iosCallbackFn : androidCallbackFN;
//endregion

const isAliyunTokenInValid = token => {
    const isEmpty = _.isEmpty(token) || !_.has(token, "token") || _.isEmpty(token.token) || !_.has(token, "expireTime") || !_.isNumber(token.expireTime);
    if (!isEmpty) {
        //test if the token is expired
        const expireTime = token.expireTime;
        const expireMoment = moment.unix(expireTime);
        return expireMoment.isBefore(new moment());
    }

    return true;
};

const isAliyunTokenWillExpire = token => {
    const isInValid = isAliyunTokenInValid(token);
    if (!isInValid) {
        //test if the token is expired
        const expireTime = token.expireTime;
        const expireMoment = moment.unix(expireTime);
        return expireMoment.add(-12, "hours").isBefore(new moment());
    }

    return true;
};

const refreshTokenAsync = (successCallback, failureCallback) => {
    ApiService.post("/web/app/refreshaliyuntoken", {}, successCallback, failureCallback);
};

const refreshSingSoundTokenAsync = (successCallback, failureCallback) => {
    //ApiService.post("/web/app/generatesingsoundtoken", {}, successCallback, failureCallback);
    if (failureCallback) {
        failureCallback(new Error("sing sound deprecated"));
    }
};

const commonHandleResult = (response, resolve, reject) => {
    //do some convert for response since the native code return raw json back
    try {
        internalInfoObj.customHandler.handleResult(internalInfoObj, response, resolve, reject);
    } catch (e) {
        reject(e);
    }
};

const _moduleInit = config => {
    return new Promise((resolve, reject) => {
        let initFailed = false;
        const initAndResolve = () => {
            if (initFailed) {
                try {
                    if (_.has(config, "initFailCallback") && _.isFunction(config.initFailCallback)) {
                        config.initFailCallback("", t(`${i18nKey}:InitError`), t(`${i18nKey}:Return`));
                        reject();
                    }
                } catch (e) {
                    DebugLogger.log(e);
                }
            } else {
                RNSpeechRecognize.init(config);
                resolve();
            }
        };
        if (!_.isEmpty(AppConfig.PROXY_SERVICE_BASE)) {
            config.proxyAddress = AppConfig.PROXY_SERVICE_BASE;
        } else if (!_.isEmpty(SessionHelper.get("serverBaseUrl"))) {
            config.proxyAddress = SessionHelper.get("serverBaseUrl");
        }

        if (!_.isEmpty(config.proxyAddress)) {
            //remove http, https scheme, we do not need it
            config.proxyAddress = config.proxyAddress.replace("http://", "").replace("https://", "");
        }

        if (config.useProxy === undefined || config.useProxy === null) {
            if (!_.isUndefined(AppConfig.SPEECH_RECOGNIZE_USE_PROXY_SERVER)) {
                config.useProxy = AppConfig.SPEECH_RECOGNIZE_USE_PROXY_SERVER === 1;
            } else if (!_.isUndefined(SessionHelper.get("useOurServer"))) {
                config.useProxy = SessionHelper.get("useOurServer");
            }
        }

        if (config.forReview) {
            if (config.reviewServiceType === reviewServiceTypeEnum.SingSound) {
                //need first get token for aliyun
                //now the token is passed by  the homework unit load screenview
                const setSingSoundToken = () => {
                    config.token = internalInfoObj.singSoundToken.token;
                    config.expireTime = internalInfoObj.singSoundToken.expireTime;
                    config.userId = internalInfoObj.singSoundToken.userId;
                    config.signature = internalInfoObj.singSoundToken.signature;
                    config.timestamp = internalInfoObj.singSoundToken.timestamp;
                };
                if (isAliyunTokenInValid(internalInfoObj.singSoundToken) && SessionHelper.isNetworkConnected()) {
                    refreshSingSoundTokenAsync(
                        singSoundToken => {
                            internalInfoObj.singSoundToken = singSoundToken;
                            setSingSoundToken();
                            initAndResolve();
                        },
                        err => {
                            initFailed = true;
                            reject();
                        }
                    );
                } else {
                    setSingSoundToken();
                    initAndResolve();
                }
            } else {
                initAndResolve();
            }
        } else {
            if (config.recognizeServiceType === recognizeServiceTypeEnum.AliYunIAT) {
                //need first get token for aliyun
                //now the token is passed by  the homework unit load screenview
                const setAliYunToken = () => {
                    config.token = internalInfoObj.aliYunToken.token;
                    config.expireTime = internalInfoObj.aliYunToken.expireTime;
                };
                if (isAliyunTokenInValid(internalInfoObj.aliYunToken) && SessionHelper.isNetworkConnected()) {
                    refreshTokenAsync(
                        ({ aliYunToken, aliYunIntToken }) => {
                            internalInfoObj.aliYunToken = aliYunToken;
                            internalInfoObj.aliYunIntToken = aliYunIntToken;
                            setAliYunToken();
                            initAndResolve();
                        },
                        err => {
                            initFailed = true;
                        }
                    );
                } else {
                    setAliYunToken();
                    initAndResolve();
                }
            } else if (config.recognizeServiceType === recognizeServiceTypeEnum.AliYunIntIAT) {
                //need first get token for aliyun
                //now the token is passed by  the homework unit load screenview
                const setAliYunIntToken = () => {
                    config.token = internalInfoObj.aliYunIntToken.token;
                    config.expireTime = internalInfoObj.aliYunIntToken.expireTime;
                };
                if (isAliyunTokenInValid(internalInfoObj.aliYunIntToken) && SessionHelper.isNetworkConnected()) {
                    refreshTokenAsync(
                        ({ aliYunToken, aliYunIntToken }) => {
                            internalInfoObj.aliYunToken = aliYunToken;
                            internalInfoObj.aliYunIntToken = aliYunIntToken;
                            setAliYunIntToken();
                            initAndResolve();
                        },
                        err => {
                            initFailed = true;
                            reject();
                        }
                    );
                } else {
                    setAliYunIntToken();
                    initAndResolve();
                }
            } else {
                initAndResolve();
            }
        }

        //refresh token when will expire
        if ((isAliyunTokenWillExpire(internalInfoObj.aliYunIntToken) || isAliyunTokenWillExpire(internalInfoObj.aliYunToken)) && SessionHelper.isNetworkConnected()) {
            refreshTokenAsync(
                ({ aliYunToken, aliYunIntToken }) => {
                    internalInfoObj.aliYunToken = aliYunToken;
                    internalInfoObj.aliYunIntToken = aliYunIntToken;
                },
                err => {
                    DebugLogger.log(err);
                }
            );
        }
        //refresh token when will expire
        /*
        if (isAliyunTokenWillExpire(internalInfoObj.singSoundToken) && SessionHelper.isNetworkConnected()) {
            refreshSingSoundTokenAsync(
                singSoundToken => {
                    internalInfoObj.singSoundToken = singSoundToken;
                },
                err => {
                    DebugLogger.log(err);
                }
            );
        }
        */
    });
};

const baseInit = config => {
    //config: {serviceType:0, ...}
    //now support wavFilePath enableSpeechRecognize in config
    // {
    //      serviceType: NativeAzure.serviceTypeEnum.Azure, //azure
    //      subscriptionKey: '',
    //      enableSpeechRecognize: true,//not set , default true
    //      wavFilePath: '',
    // }

    if (_.has(config, "recognizeTimeout") && _.isNumber(config.recognizeTimeout)) {
        internalInfoObj.recognizeTimeout = config.recognizeTimeout;
    } else {
        internalInfoObj.recognizeTimeout = DEFAULT_RECOGNIZE_TIME_OUT;
    }

    if (_.has(config, "lang")  && config.lang && config.lang !== "") {
        internalInfoObj.lang = config.lang;
    }

    internalInfoObj.customHandler = config.customHandler;
    internalInfoObj.forReview = config.forReview;
    if (_.has(config, "reviewServiceType") && internalInfoObj.forReview) {
        internalInfoObj.reviewServiceType = config.reviewServiceType;
    } else if (_.has(config, "recognizeServiceType") && !internalInfoObj.forReview) {
        internalInfoObj.recognizeServiceType = config.recognizeServiceType;
    } else {
        config.reviewServiceType = AppConfig.SPEECH_RECOGNIZE_SERVICE_REVIEW_TYPE;
        config.recognizeServiceType = AppConfig.SPEECH_RECOGNIZE_SERVICE_TYPE;
        internalInfoObj.reviewServiceType = config.reviewServiceType;
        internalInfoObj.recognizeServiceType = config.recognizeServiceType;
        internalInfoObj.forReview = config.forReview;
    }

    if (_.has(config, "wavFilePath") && config.wavFilePath && config.wavFilePath !== "") {
        internalInfoObj.wavFilePath = config.wavFilePath;
    }
    if (_.has(config, "enableSpeechRecognize") && _.isBoolean(config.enableSpeechRecognize)) {
        internalInfoObj.enableSpeechRecognize = config.enableSpeechRecognize;
    }
    if (_.has(config, "backgroundStopRecordCallback") && _.isFunction(config.backgroundStopRecordCallback)) {
        internalInfoObj.backgroundStopRecordCallback = config.backgroundStopRecordCallback;
    }

    SpeechRecognition.lastConfig = config;
    return new Promise((resolve, reject) => {
        try {
            if (Platform.OS === "android") {
                PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.RECORD_AUDIO, {
                    title: t(`${i18nKey}:NeedPermissionTitle`),
                    message: t(`${i18nKey}:NeedPermissionContent`),
                    buttonNeutral: t(`${i18nKey}:AskMeLater`),
                    buttonNegative: t(`${i18nKey}:Cancel`),
                    buttonPositive: t(`${i18nKey}:OK`),
                })
                    .then(granted => {
                        if (granted === PermissionsAndroid.RESULTS.GRANTED) {
                            DebugLogger.log("You can record audio");
                            _moduleInit(config)
                                .then(resolve)
                                .catch(reject);
                        } else {
                            DebugLogger.log("record audio permission denied");
                            try {
                                if (_.has(config, "rejectPermissionCallback") && _.isFunction(config.rejectPermissionCallback)) {
                                    config.rejectPermissionCallback();
                                    reject();
                                }
                            } catch (e) {
                                DebugLogger.log(e);
                                reject(e);
                            }
                        }
                    })
                    .catch(err => DebugLogger.log(err));
            } else if (Platform.OS === "ios") {
                const handler = callbackHandler(granted => {
                    if (granted === 1) {
                        DebugLogger.log("You can record audio");
                        _moduleInit(config)
                            .then(resolve)
                            .catch(reject);
                    } else {
                        DebugLogger.log("record audio permission denied");
                        Alert.alert(t(`${i18nKey}:NeedPermissionTitle`), t(`${i18nKey}:NeedPermissionContent`), [
                            {
                                text: t(`${i18nKey}:Cancel`),
                                onPress: () => {
                                    try {
                                        if (_.has(config, "rejectPermissionCallback") && _.isFunction(config.rejectPermissionCallback)) {
                                            config.rejectPermissionCallback();
                                            reject();
                                        }
                                    } catch (e) {
                                        DebugLogger.log(e);
                                    }
                                },
                                style: "cancel",
                            },
                            {
                                text: t(`${i18nKey}:OK`),
                                onPress: () => {
                                    try {
                                        RNSpeechRecognize.openSetting();
                                        reject();
                                    } catch (e) {
                                        DebugLogger.log(e);
                                        reject(e);
                                    }
                                },
                            },
                        ]);
                    }
                });
                RNSpeechRecognize.requirePermission(handler);
            } else if (Platform.OS === "web") {
                const handler = callbackHandler(granted => {
                    if (granted) {
                        DebugLogger.log("You can record audio");
                        _moduleInit(config)
                            .then(resolve)
                            .catch(reject);
                    } else {
                        DebugLogger.log("record audio permission denied");
                        Alert.alert(t(`${i18nKey}:NeedPermissionTitle`), t(`${i18nKey}:NeedPermissionContent`), [
                            {
                                text: t(`${i18nKey}:Cancel`),
                                onPress: () => {
                                    try {
                                        if (_.has(config, "rejectPermissionCallback") && _.isFunction(config.rejectPermissionCallback)) {
                                            config.rejectPermissionCallback();
                                            reject();
                                        }
                                    } catch (e) {
                                        DebugLogger.log(e);
                                    }
                                },
                                style: "cancel",
                            },
                        ]);
                    }
                });

                if (config.noRequirePermission) {
                    setTimeout(() => handler(true), 100);
                } else {
                    // NOTE: web点录音的时候再请求权限
                    //setTimeout(() => handler(true), 100);
                    RNSpeechRecognize.requirePermission(handler);
                }
            }

            //AppState.addEventListener("change", handleAppStateChange);

            if (!config.forReview) {
                DebugLogger.log("SpeechRecognition init, serviceType info 0: Xunfei ASR, 1: Xunfei IAT(60s), 2: Aliyun IAT(60s), 3: Aliyun IAT INT(60s), 4: Azure US");
                DebugLogger.log("SpeechRecognition init, Current Service Type => " + config.recognizeServiceType);
            } else {
                DebugLogger.log("SpeechRecognition init, serviceType info 1: Xunfei 语音评测 , 2: 先声语音评测");
                DebugLogger.log("SpeechRecognition init, Current Service Type => " + config.reviewServiceType);
            }

            DebugLogger.log("SpeechRecognition Config");
            DebugLogger.log(config);
        } catch (err) {
            DebugLogger.log(err);
            reject(err);
        }
    });
};

const handleAppStateChange = nextAppState => {
    if (internalInfoObj.currentAppState.match(/inactive|background/) && nextAppState === "active") {
        DebugLogger.log("App has come to the foreground!");
    } else {
        DebugLogger.log("App has come to the background!");
        try {
            if (_.has(internalInfoObj, "backgroundStopRecordCallback") && _.isFunction(internalInfoObj.backgroundStopRecordCallback)) {
                //RNSpeechRecognize.stopRecognize();
                internalInfoObj.backgroundStopRecordCallback();
            }
        } catch (e) {
            DebugLogger.log(e);
        }
    }
    internalInfoObj.currentAppState = nextAppState;
};

const assignKeysFromAppSetting = config => {
    let isEnCase = false;
    if (_.has(config, "lang") && config.lang && config.lang !== "") {
        isEnCase = config.lang === Constants.SPEECH_LANG.en;
    }
    if (config.forReview) {
        switch (config.reviewServiceType) {
            case reviewServiceTypeEnum.XunFei:
                return {
                    forReview: true,
                    serviceType: reviewServiceTypeEnum.XunFei,
                    appId: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_ID,
                    apiSecret: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_SECRET,
                    apiKey: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_KEY,
                    ...config,
                };

            case reviewServiceTypeEnum.Azure:
                return {
                    forReview: true,
                    serviceType: reviewServiceTypeEnum.Azure,
                    subscriptionKey: AppConfig.SPEECH_RECOGNIZE_AZURE_SUBSCRIBE_KEY,
                    region: AppConfig.SPEECH_RECOGNIZE_AZURE_REGION,
                    ...config,
                };
            case reviewServiceTypeEnum.SingSound:
                return {
                    forReview: true,
                    serviceType: reviewServiceTypeEnum.SingSound,
                    appKey: AppConfig.SPEECH_RECOGNIZE_SING_SOUND_APP_ID,
                    ...config,
                };
            default:
                return config;
        }
    } else {
        switch (config.recognizeServiceType) {
            case recognizeServiceTypeEnum.XunFeiASR:
                return {
                    forReview: false,
                    serviceType: recognizeServiceTypeEnum.XunFeiASR,
                    appId: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_ASR_ID,
                    apiKey: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_ASR_KEY,
                    ...config,
                };
            case recognizeServiceTypeEnum.XunFeiIAT:
                return {
                    forReview: false,
                    serviceType: recognizeServiceTypeEnum.XunFeiIAT,
                    appId: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_IAT_ID,
                    apiSecret: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_IAT_SECRET,
                    apiKey: AppConfig.SPEECH_RECOGNIZE_XUN_FEI_IAT_KEY,
                    ...config,
                };
            case recognizeServiceTypeEnum.AliYunIAT:
                return {
                    forReview: false,
                    serviceType: recognizeServiceTypeEnum.AliYunIAT,
                    appKey: isEnCase ? AppConfig.SPEECH_RECOGNIZE_ALI_YUN_IAT_EN_APP_KEY : AppConfig.SPEECH_RECOGNIZE_ALI_YUN_IAT_APP_KEY,
                    ...config,
                };
            case recognizeServiceTypeEnum.AliYunIntIAT:
                return {
                    forReview: false,
                    serviceType: recognizeServiceTypeEnum.AliYunIntIAT,
                    appKey: isEnCase ? AppConfig.SPEECH_RECOGNIZE_ALI_YUN_INT_IAT_EN_APP_KEY : AppConfig.SPEECH_RECOGNIZE_ALI_YUN_INT_IAT_APP_KEY,
                    ...config,
                };
            case recognizeServiceTypeEnum.AzureIAT:
                return {
                    forReview: false,
                    serviceType: recognizeServiceTypeEnum.AzureIAT,
                    subscriptionKey: AppConfig.SPEECH_RECOGNIZE_AZURE_IAT_SUBSCRIBE_KEY,
                    region: AppConfig.SPEECH_RECOGNIZE_AZURE_IAT_REGION,
                    ...config,
                };
            default:
                return config;
        }
    }
};

const SpeechRecognition = {
    ...RNSpeechRecognize,
    init: baseInit,
    initWithoutKey: config => {
        let serviceTypeManuallySet = true;
        if (!_.has(config, "serviceType")) {
            if (config.forReview) {
                config.reviewServiceType = AppConfig.SPEECH_RECOGNIZE_SERVICE_REVIEW_TYPE;
            } else {
                config.recognizeServiceType = AppConfig.SPEECH_RECOGNIZE_SERVICE_TYPE;
            }
            serviceTypeManuallySet = false;
        }

        DebugLogger.log(AppConfig);
        try {
            if (!serviceTypeManuallySet && _.has(AppConfig, "SPEECH_RECOGNIZE_AUTO_CHOOSE_SERVICE") && _.toString(AppConfig.SPEECH_RECOGNIZE_AUTO_CHOOSE_SERVICE) === "1") {
                //根据所在地自动切换使用阿里云或者阿里云国际
                const isInChina = LocationHelper.getIsInChina();

                DebugLogger.log(`Auto Choose Speech Service Type: is in china ${isInChina}`);
                config.recognizeServiceType = isInChina ? recognizeServiceTypeEnum.AliYunIAT : recognizeServiceTypeEnum.AliYunIntIAT;
                const mergedConfig = assignKeysFromAppSetting(config);
                return baseInit(mergedConfig);
            } else {
                const mergedConfig = assignKeysFromAppSetting(config);
                return baseInit(mergedConfig);
            }
        } catch (e) {
            const mergedConfig = assignKeysFromAppSetting(config);
            return baseInit(mergedConfig);
        }
    },
    getCurrentState: fn => {
        const handler = callbackHandler(fn);
        RNSpeechRecognize.getCurrentState(handler);
    },
    getHasWavFile: (orgPath, fn) => {
        const handler = callbackHandler(fn);
        RNSpeechRecognize.getHasWavFile(orgPath, handler);
    },
    getCurrentVolume: fn => {
        const handler = callbackHandler(fn);
        RNSpeechRecognize.getCurrentVolume(handler);
    },
    /**
     * get wav file path by path you set, use for multi-record case
     * @param wavPath
     * @param fn
     */
    getWavFilePath: (wavPath, fn) => {
        const handler = callbackHandler(fn);
        RNSpeechRecognize.getWavFilePath(wavPath, handler);
    },
    getWavFilePathNoCheck: (wavPath, fn) => {
        const handler = callbackHandler(fn);
        RNSpeechRecognize.getWavFilePathNoCheck(wavPath, handler);
    },
    getLastWavFilePath: fn => {
        const handler = callbackHandler(fn);
        RNSpeechRecognize.getLastWavFilePath(handler);
    },
    setToken: ({ aliYunToken, aliYunIntToken, singSoundToken }) => {
        DebugLogger.log("SpeechRecognition set token");
        internalInfoObj.aliYunToken = aliYunToken;
        internalInfoObj.aliYunIntToken = aliYunIntToken;
        internalInfoObj.singSoundToken = singSoundToken;
    },
    setLang: (lang) => {
        DebugLogger.log("SpeechRecognition set Lang");
        internalInfoObj.lang = lang;
    },
    getToken: () => {
        const { aliYunToken, aliYunIntToken, singSoundToken } = internalInfoObj;
        return { aliYunToken, aliYunIntToken, singSoundToken };
    },
    setReferenceText: text => {
        RNSpeechRecognize.setReferenceText(text);
        internalInfoObj.referenceText = text;
        internalInfoObj.customHandler.setReferenceText(internalInfoObj, RNSpeechRecognize.setReferenceText);
        //DebugLogger.log(text)
    },
    setReferenceSyllables: syllableArray => {
        internalInfoObj.syllableArray = syllableArray;
        internalInfoObj.customHandler.setReferenceSyllables(internalInfoObj, RNSpeechRecognize.setReferenceText);
        //DebugLogger.log(text)
    },
    setReferenceMarkText: text => {
        internalInfoObj.referenceMarkText = text;
        //DebugLogger.log(text)
    },
    setEnableSpeechRecognize: enableOrNot => {
        RNSpeechRecognize.setEnableSpeechRecognize(enableOrNot);
        internalInfoObj.enableSpeechRecognize = enableOrNot;
        //DebugLogger.log(enableOrNot)
    },
    setWavFilePath: wavFilePath => {
        RNSpeechRecognize.setWavFilePath(wavFilePath);
        internalInfoObj.wavFilePath = wavFilePath;
        //DebugLogger.log(enableOrNot)
    },
    setRecognizeTimeout: timeout => {
        // console.log(`recognize timeout set:${timeout}`);
        if (_.isNumber(timeout)) {
            internalInfoObj.recognizeTimeout = timeout;
        } else {
            internalInfoObj.recognizeTimeout = DEFAULT_RECOGNIZE_TIME_OUT;
        }
    },
    startRecognize: (onStarted) => {
        return new Promise((resolve, reject) => {
            const orgPromise = RNSpeechRecognize.startRecognize(onStarted);
            orgPromise
                .then(response => {
                    //resolve when do not need recognize
                    if (!internalInfoObj.enableSpeechRecognize) {
                        resolve({ wordResultArray: [] });
                    } else {
                        commonHandleResult(response, resolve, reject);
                    }
                })
                .catch(error => {
                    reject(error);
                });
        });
    },
    recognizeFile: (file, orgRecognize) => {
        if (orgRecognize) {
            return RNSpeechRecognize.recognizeFile(file);
        }
        return new Promise((resolve, reject) => {
            const recognizeFn = () => {
                const orgPromise = RNSpeechRecognize.recognizeFile(file);
                let callbackCalled = false;
                // console.log("timeout sec: " + internalInfoObj.recognizeTimeout)
                setTimeout(() => {
                    // console.log("timeout")
                    if (!callbackCalled) {
                        reject({ message: "Client timeout, abort.", code: Constants.CODES.CLIENT_TIMEOUT });
                    }
                }, 1000 * internalInfoObj.recognizeTimeout);

                orgPromise
                    .then(response => {
                        callbackCalled = true;
                        commonHandleResult(response, resolve, reject);
                    })
                    .catch(error => {
                        callbackCalled = true;
                        reject(error);
                    });
            };

          recognizeFn();
        });
    },
    addEventListener,
    removeEventListener,
    removeAllListeners,
    recognizeServiceTypeEnum,
    reviewServiceTypeEnum,
    destroy: () => {
        //AppState.removeEventListener("change", handleAppStateChange);
        RNSpeechRecognize.destroy();
    },
};

SpeechRecognition.propTypes = {
    initWithSubscribeKey: PropTypes.func,
    init: PropTypes.func,
    initWithoutKey: PropTypes.func,
    destroy: PropTypes.func,
    setSubscriptionKey: PropTypes.func,
    setAutoStopSec: PropTypes.func,
    setReferenceText: PropTypes.func,
    setReferenceSyllables: PropTypes.func,
    getCurrentState: PropTypes.func,
    getHasWavFile: PropTypes.func,
    getIsRecording: PropTypes.func,
    startRecognize: PropTypes.func,
    stopRecognize: PropTypes.func,
    setRecognizeTimeout: PropTypes.func,
    recognizeFile: PropTypes.func,
    getLastWavFilePath: PropTypes.func,
    getCurrentVolume: PropTypes.func,
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
    removeAllListeners: PropTypes.func,
    setEnableSpeechRecognize: PropTypes.func,
    setWavFilePath: PropTypes.func,
    getWavFilePath: PropTypes.func, //get wav file path by sending the wav path you set, this is for you use this component for multiple audio record case, you can set wav file path, record, and get it later
    getWavFilePathNoCheck: PropTypes.func,
};

export default SpeechRecognition;
